import { GLOBAL_TEAM_FILTER, PROJECT_FILTERS_KEY } from './constants';

const getProjectFiltersObject = () => {
  try {
    // eslint-disable-next-line no-undef
    const storedValue = localStorage?.getItem(PROJECT_FILTERS_KEY);
    if (!storedValue || storedValue === 'null' || storedValue === '') {
      return null;
    }
    return JSON.parse(storedValue);
  } catch (error) {
    return null;
  }
};

const setProjectFiltersObject = (filtersObject) => {
  try {
    if (!filtersObject || typeof filtersObject !== 'object') {
      // eslint-disable-next-line no-undef
      localStorage?.removeItem(PROJECT_FILTERS_KEY);
      return;
    }

    // eslint-disable-next-line no-undef
    localStorage?.setItem(PROJECT_FILTERS_KEY, JSON.stringify(filtersObject));
  } catch {
    // Silent error handling
  }
};

const getProjectFilterData = (projectId) => {
  if (!projectId) return null;

  const filtersObject = getProjectFiltersObject();

  if (!filtersObject || typeof filtersObject !== 'object') {
    return null;
  }

  return filtersObject?.[String(projectId)] || null;
};

const setProjectFilterData = (projectId, filterData) => {
  if (!projectId) return;
  const filtersObject = getProjectFiltersObject() || {};
  const projectKey = String(projectId);

  if (!filterData) {
    // Remove project data if filterData is null/undefined
    delete filtersObject[projectKey];
  } else {
    // Always set project data with initialization flag
    filtersObject[projectKey] = {
      teamIds: filterData?.teamIds || [],
      isMeMode: Boolean(filterData?.isMeMode),
      hasBeenInitialized: Boolean(filterData?.hasBeenInitialized),
    };
  }
  if (Object.keys(filtersObject)?.length === 0) {
    setProjectFiltersObject(null);
  } else {
    setProjectFiltersObject(filtersObject);
  }
};

export const getProjectTeamFilter = (projectId) => {
  if (!projectId) return null;

  try {
    const projectData = getProjectFilterData(projectId);

    if (!projectData) {
      return null;
    }

    if (
      projectData?.hasBeenInitialized &&
      Array.isArray(projectData?.teamIds)
    ) {
      return projectData?.teamIds?.filter(
        (id) => id !== null && id !== undefined && String(id)?.trim() !== '',
      );
    }

    // Backward compatibility for projects without hasBeenInitialized flag
    if (
      projectData?.teamIds &&
      Array.isArray(projectData?.teamIds) &&
      projectData?.teamIds?.length > 0
    ) {
      return projectData?.teamIds?.filter(
        (id) => id !== null && id !== undefined && String(id)?.trim() !== '',
      );
    }

    return null;
  } catch (error) {
    return null;
  }
};

export const setProjectTeamFilter = (projectId, teamIds) => {
  if (!projectId) return;

  try {
    const currentData = getProjectFilterData(projectId) || {};
    const filteredTeamIds =
      teamIds?.filter(
        (id) => id !== null && id !== undefined && String(id)?.trim() !== '',
      ) || [];

    setProjectFilterData(projectId, {
      ...currentData,
      teamIds: filteredTeamIds,
      hasBeenInitialized: true,
    });
  } catch {
    // Silent error handling
  }
};

export const getProjectMeMode = (projectId) => {
  if (!projectId) return false;

  try {
    const projectData = getProjectFilterData(projectId);
    return Boolean(projectData?.isMeMode);
  } catch (error) {
    return false;
  }
};

export const setProjectMeMode = (projectId, enabled) => {
  if (!projectId) return;

  try {
    const currentData = getProjectFilterData(projectId) || {};

    setProjectFilterData(projectId, {
      ...currentData,
      isMeMode: Boolean(enabled),
      hasBeenInitialized: true,
    });
  } catch {
    // Silent error handling
  }
};

export const migrateToProjectSpecificStorage = (projectId) => {
  if (!projectId) return { teamFilter: null, meMode: false };

  try {
    const existingTeamFilter = getProjectTeamFilter(projectId);
    const existingMeMode = getProjectMeMode(projectId);

    if (existingTeamFilter !== null || existingMeMode) {
      return {
        teamFilter: existingTeamFilter,
        meMode: existingMeMode,
      };
    }
    let migratedTeamFilter = null;
    let migratedMeMode = false;

    try {
      // eslint-disable-next-line no-undef
      const oldTeamFilterKey = `PROJECT_TEAM_FILTER_${projectId}`;
      // eslint-disable-next-line no-undef
      const oldMeModeKey = `PROJECT_ME_MODE_${projectId}`;

      // eslint-disable-next-line no-undef
      const oldTeamFilter = localStorage?.getItem(oldTeamFilterKey);
      if (oldTeamFilter && oldTeamFilter !== 'null' && oldTeamFilter !== '') {
        const teamIds = oldTeamFilter
          .split(',')
          .filter((id) => id?.trim() !== '');

        if (teamIds?.length > 0) {
          migratedTeamFilter = teamIds;
        }
        // eslint-disable-next-line no-undef
        localStorage?.removeItem(oldTeamFilterKey);
      }

      // eslint-disable-next-line no-undef
      const oldMeMode = localStorage?.getItem(oldMeModeKey);
      if (oldMeMode === 'true') {
        migratedMeMode = true;
        // eslint-disable-next-line no-undef
        localStorage?.removeItem(oldMeModeKey);
      }
    } catch {
      // Silent error handling
    }

    if (migratedTeamFilter === null && !migratedMeMode) {
      // eslint-disable-next-line no-undef
      const globalTeamFilter = localStorage?.getItem(GLOBAL_TEAM_FILTER);
      if (
        globalTeamFilter &&
        globalTeamFilter !== 'null' &&
        globalTeamFilter !== ''
      ) {
        const teamIds = globalTeamFilter
          .split(',')
          .filter((id) => id?.trim() !== '');

        if (teamIds?.length > 0) {
          migratedTeamFilter = teamIds;
        }
      }
    }

    if (migratedTeamFilter !== null || migratedMeMode) {
      const currentData = getProjectFilterData(projectId) || {};
      setProjectFilterData(projectId, {
        ...currentData,
        teamIds: migratedTeamFilter || currentData?.teamIds || [],
        isMeMode: migratedMeMode || currentData?.isMeMode || false,
        hasBeenInitialized: true,
      });
    }

    return {
      teamFilter: migratedTeamFilter,
      meMode: migratedMeMode,
    };
  } catch (error) {
    return { teamFilter: null, meMode: false };
  }
};

export const clearProjectFilters = (projectId) => {
  if (!projectId) return;

  try {
    const filtersObject = getProjectFiltersObject() || {};
    const projectKey = String(projectId);
    delete filtersObject[projectKey];
    if (Object.keys(filtersObject)?.length === 0) {
      setProjectFiltersObject(null);
    } else {
      setProjectFiltersObject(filtersObject);
    }

    try {
      // eslint-disable-next-line no-undef
      localStorage?.removeItem(`PROJECT_TEAM_FILTER_${projectId}`);
      // eslint-disable-next-line no-undef
      localStorage?.removeItem(`PROJECT_ME_MODE_${projectId}`);
    } catch {
      // Silent error handling
    }
  } catch {
    // Silent error handling
  }
};

export const getAllProjectFilters = () => {
  try {
    return getProjectFiltersObject() || {};
  } catch (error) {
    return {};
  }
};

export const getAllLegacyProjectFilterKeys = () => {
  try {
    const keys = {
      teamFilters: [],
      meModes: [],
    };

    // eslint-disable-next-line no-undef
    for (let i = 0; i < localStorage?.length; i += 1) {
      // eslint-disable-next-line no-undef
      const key = localStorage?.key(i);

      if (key?.startsWith('PROJECT_TEAM_FILTER_')) {
        keys.teamFilters.push(key);
      } else if (key?.startsWith('PROJECT_ME_MODE_')) {
        keys.meModes.push(key);
      }
    }

    return keys;
  } catch (error) {
    return { teamFilters: [], meModes: [] };
  }
};

export const cleanupLegacyProjectFilterKeys = () => {
  try {
    const legacyKeys = getAllLegacyProjectFilterKeys();
    let cleanupCount = 0;

    // Remove legacy team filter keys
    legacyKeys?.teamFilters?.forEach((key) => {
      try {
        // eslint-disable-next-line no-undef
        localStorage?.removeItem(key);
        cleanupCount += 1;
      } catch {
        // Silent error handling
      }
    });

    // Remove legacy me mode keys
    legacyKeys?.meModes?.forEach((key) => {
      try {
        // eslint-disable-next-line no-undef
        localStorage?.removeItem(key);
        cleanupCount += 1;
      } catch {
        // Silent error handling
      }
    });

    return cleanupCount;
  } catch (error) {
    return 0;
  }
};

export const hasProjectBeenInitialized = (projectId) => {
  if (!projectId) return false;

  try {
    const projectData = getProjectFilterData(projectId);
    return Boolean(projectData?.hasBeenInitialized);
  } catch (error) {
    return false;
  }
};

export const initializeProjectFilters = (projectId, defaultTeamIds = []) => {
  if (!projectId) return { teamFilter: [], meMode: false };

  const migrated = migrateToProjectSpecificStorage(projectId);

  if (migrated.teamFilter !== null) {
    return migrated;
  }

  const teamFilter = defaultTeamIds?.length > 0 ? defaultTeamIds : [];
  const meMode = false;

  if (teamFilter?.length > 0) {
    setProjectTeamFilter(projectId, teamFilter);
  }

  return { teamFilter, meMode };
};
